// WhatsApp deep-link builder for Forwaro's B2B inquiry flow.
// Official Forwaro WhatsApp Business number (China, +86). Stored in
// human-readable form; sanitizeNumber() strips it to the digits wa.me needs.

export const FORWARO_WHATSAPP_NUMBER = "+86 139 2584 2868";

// Strip the "+", dashes, parentheses and spaces WhatsApp won't accept.
const sanitizeNumber = (raw: string): string =>
  raw.replace(/[^0-9]/g, "");

export interface WhatsAppPayload {
  /** Product SKU e.g. "SF001". Prepended to the message when present. */
  sku?: string;
  /** Free-form note from the visitor. */
  message?: string;
  /** Buyer country, for Forwaro's sales team to route the conversation. */
  country?: string;
  /** Override destination number when needed (e.g. regional reps). */
  overrideNumber?: string;
}

/**
 * Build a `https://wa.me/<digits>?text=<encoded>` URL.
 *
 * If no number is configured, returns an empty-href `#` so the UI can
 * render a disabled state rather than a broken link.
 */
export const buildWhatsAppUrl = ({
  sku,
  message,
  country,
  overrideNumber,
}: WhatsAppPayload = {}): string => {
  const number = sanitizeNumber(overrideNumber ?? FORWARO_WHATSAPP_NUMBER);

  if (!number) return "#";

  const lines: string[] = [];
  lines.push("Hello Forwaro,");
  if (sku) lines.push(`I'd like more information on product ${sku}.`);
  if (country) lines.push(`Shipping destination: ${country}.`);
  if (message) lines.push(message);
  if (lines.length === 1) {
    lines.push("I'd like to discuss a potential order.");
  }

  const encoded = encodeURIComponent(lines.join("\n"));
  return `https://wa.me/${number}?text=${encoded}`;
};

/**
 * Returns true when the WhatsApp number is configured and the deep link
 * will actually open a real conversation.
 */
export const isWhatsAppConfigured = (overrideNumber?: string): boolean => {
  const number = sanitizeNumber(overrideNumber ?? FORWARO_WHATSAPP_NUMBER);
  return Boolean(number);
};
